import React, {useContext, useState} from 'react';
import {NavLink} from 'react-router-dom';
import {CSSTransition} from 'react-transition-group';
import classNames from 'classnames';
import {Ripple} from "primereact/ripple";
import {Badge} from 'primereact/badge';
import { I18n } from "aws-amplify/utils";
import {isFormateur} from "./Utils";
import {UserContext} from "./app-context";

const AppSubmenu = (props) => {

    const [activeIndex, setActiveIndex] = useState(null);

    const onMenuItemClick = (event, item, index) => {
        //avoid processing disabled items
        if (item.disabled) {
            event.preventDefault();
            return true;
        }

        //execute command
        if (item.command) {
            item.command({ originalEvent: event, item: item });
        }

        if (index === activeIndex)
            setActiveIndex(null);
        else
            setActiveIndex(index);

        if (props.onMenuItemClick) {
            props.onMenuItemClick({
                originalEvent: event,
                item: item
            });
        }
    }

    const renderLinkContent = (item) => {
        let submenuIcon = item.items && <i className="pi pi-fw pi-angle-down menuitem-toggle-icon" aria-hidden="true"></i>;
        let badge = item.badge && <Badge value={item.badge} />

        return (
            <React.Fragment>
                <i className={item.icon} aria-hidden="true"></i>
                <span>{item.label}</span>
                {item.opensNewTab && <span className="sr-only"> {I18n.get("link.opensnewtab")}</span>}
                {submenuIcon}
                {badge}
                <Ripple/>
            </React.Fragment>
        );
    }

    // This is site navigation, not an application menu (no submenus in the current menu config,
    // no command palette semantics) - role="menu"/"menuitem" would require implementing the full
    // ARIA menu keyboard pattern (arrow keys, Home/End, roving tabindex) to not mislead assistive
    // tech about what's available. Native <ul>/<li>/<a> semantics already give correct Tab-order
    // keyboard access for free, so we rely on those instead (WCAG 4.1.2).
    const renderLink = (item, i) => {
        let content = renderLinkContent(item);
        const ariaLabel = item.opensNewTab ? `${item.label} ${I18n.get("link.opensnewtab")}` : item.label;

        if (item.to) {
            return (
                <NavLink aria-label={ariaLabel} aria-expanded={item.items ? activeIndex === i : undefined} className={(navData) => (navData.isActive ? "p-ripple router-link-active router-link-exact-active" : 'p-ripple')} to={item.to} onClick={(e) => onMenuItemClick(e, item, i)} exact="true" target={item.target}>
                    {content}
                </NavLink>
            )
        }
        else {
            return (
                <a tabIndex="0" aria-label={ariaLabel} aria-expanded={item.items ? activeIndex === i : undefined} href={item.url} className="p-ripple" onClick={(e) => onMenuItemClick(e, item, i)} target={item.target}>
                    {content}
                </a>
            );
        }
    }

    let items = props.items && props.items.map((item, i) => {
        let active = activeIndex === i;
        let styleClass = classNames(item.badgeStyleClass, {'layout-menuitem-category': props.root, 'active-menuitem': active && !item.to });

        if(props.root) {
            return (
                <li className={styleClass} key={i}>
                    {props.root === true && <React.Fragment>
                        <div className="layout-menuitem-root-text" aria-label={item.label}>{item.label}</div>
                        <AppSubmenu items={item.items} onMenuItemClick={props.onMenuItemClick} />
                    </React.Fragment>}
                </li>
            );
        }
        else {
            return (
                <li className={styleClass} key={i}>
                    {renderLink(item, i)}
                    <CSSTransition classNames="layout-submenu-wrapper" timeout={{ enter: 1000, exit: 450 }} in={active} unmountOnExit>
                        <AppSubmenu items={item.items} onMenuItemClick={props.onMenuItemClick} />
                    </CSSTransition>
                </li>
            );
        }
    });

    return items ? <ul className={props.className}>{items}</ul> : null;
}

export const AppMenu = (props) => {
    const authenticatedUserData = useContext(UserContext);
    const useBuildMenu = () => {
        if (authenticatedUserData?.username) {
            const cgPath = (I18n.get("language") === 'de') ? 'de/mediathek/media/allgemeine-bedingungen-meisterbrief' : 'fr/mediatheque/media/conditions-generales-brevet-maitrise';
            if (isFormateur(authenticatedUserData)) {
                return [
                    {
                        items: [
                            {label: I18n.get("menu.accueil"), icon: 'fas fa-home pi-fw', to: '/'},
                            {label: I18n.get("menu.agenda"), icon: 'fa fa-calendar-alt pi-fw', to: '/formateur-schedule'},
                            {label: I18n.get("menu.declarationfrais"), icon: 'fa fa-calendar-alt pi-fw', to: '/formateur-declaration-frais'},
                            {
                                label: I18n.get("menu.conditionsgenerales"), icon: 'fa fa-file pi-fw', opensNewTab: true, command: () => {
                                    window.open(`https://formations.cdm.lu/${cgPath}`, '_blank');
                                }
                            },
                            {label: I18n.get("menu.accessibilite"), icon: 'fa fa-universal-access pi-fw', to: '/accessibilite'}
                        ]
                    }
                ];
            } else {
                const menuCandidate = [
                    {
                        items: [
                            {label: I18n.get("menu.accueil"), icon: 'fas fa-home pi-fw', to: '/'},
                            {label: I18n.get("menu.agenda"), icon: 'fa fa-calendar-alt pi-fw', to: '/candidate-schedule'},
                            {label: I18n.get("menu.excuses"), icon: 'fa fa-file-medical pi-fw', to: '/candidate-excuses'},
                            {label: I18n.get("menu.historique"), icon: 'fa fa-history pi-fw', to: '/candidate-history'},
                            {
                                label: I18n.get("menu.conditionsgenerales"), icon: 'fa fa-file pi-fw', opensNewTab: true, command: () => {
                                    window.open(`https://formations.cdm.lu/${cgPath}`, '_blank');
                                }
                            },
                            {label: I18n.get("menu.accessibilite"), icon: 'fa fa-universal-access pi-fw', to: '/accessibilite'}
                        ]
                    }
                ];


                if (authenticatedUserData?.openInsc && authenticatedUserData?.openInsc === true) {
                    menuCandidate[0].items.splice(1, 0, {label: I18n.get("menu.inscription"), icon: 'fa fa-graduation-cap pi-fw', to: '/inscription'});
                }
                return menuCandidate;
            }
        }
    }

    return (
        <div className="layout-menu-container">
            <AppSubmenu items={useBuildMenu()} className="layout-menu" onMenuItemClick={props.onMenuItemClick} root={true} />
        </div>
    );
}


